import React from 'react';
import Immutable from 'immutable';

import CK from '~/types/contentking';

import BooleanChart from '../fields/BooleanChart';
import CanonicalTypeChart from '../fields/CanonicalTypeChart';
import ChartLayout from '~/components/patterns/charts/layouts/ChartLayout';
import ColumnName from '~/components/names/ColumnName';
import ContentPresenceChart from '../fields/ContentPresenceChart';
import DomainChart from '../fields/DomainChart';
import HealthChart from '../fields/HealthChart';
import HealthLabel from '~/components/patterns/structuredValues/HealthLabel';
import HreflangLanguageChart from '../fields/HreflangLanguageChart';
import IndexableChart from '../fields/IndexableChart';
import LoadingDots from '~/components/patterns/loaders/LoadingDots';
import NumberOfHreflangsChart from '../fields/NumberOfHreflangsChart';
import PageTypeChart from '../fields/PageTypeChart';
import RelevanceChart from '../fields/RelevanceChart';
import RobotsTxtChart from '../fields/RobotsTxtChart';
import StatusCodeChart from '../fields/StatusCodeChart';
import UrlDepthChart from '../fields/UrlDepthChart';
import WebVitalName from '~/components/app/WebVitalName';
import WebVitalsChart from '../fields/WebVitalsChart';

import {
	usePageFieldStatisticsChartQuery
} from './PageFieldStatisticsChart.gql';

import useBatchContextPagesGraphs from '~/hooks/useBatchContextPagesGraphs';
import usePollInterval from '~/hooks/usePollInterval';

import {
	getFilter
} from '~/model/pages';

import {
	WebVital
} from '~/model/webVitals';
import {
	notEmpty
} from '~/utilities/typeCheck';



type Props = {
	column: CK.PagesColumn,
	filter: any,
	websiteId: CK.WebsiteId,
}

const PageFieldStatisticsChart: React.FC<Props> = (props) => {
	const {
		column,
		filter,
		websiteId,
	} = props;

	const {
		criteria,
		isInvalid,
	} = getFilter(filter.toJS ? filter : Immutable.fromJS(filter));

	const { data, previousData, loading } = usePageFieldStatisticsChartQuery({
		context: useBatchContextPagesGraphs(websiteId),
		pollInterval: usePollInterval(60_000),
		variables: {
			fields: [column],
			filter: criteria,
			websiteId,
		},
		skip: isInvalid,
	});

	const statistics = (data ?? previousData)?.pagesStatistics?.statistics
		.filter(notEmpty)
		.find((stat) => stat.field === column)
		?.scopes.find((scope) => scope.scope === 'website')
		?.values ?? [];

	return (
		<ChartLayout
			label={(
				<PageFieldStatisticsChartLabel column={column} />
			)}
			overlay={loading && (
				<LoadingDots />
			)}
		>
			<PageFieldStatisticsChartContent
				column={column}
				data={statistics}
			/>
		</ChartLayout>
	);
};

export default PageFieldStatisticsChart;



type PageFieldStatisticsChartLabelProps = {
	column: CK.PagesColumn;
}

const PageFieldStatisticsChartLabel: React.FC<PageFieldStatisticsChartLabelProps> = (props) => {
	const {
		column,
	} = props;

	if (column === CK.PagesCommonColumn.Health) {
		return (
			<HealthLabel>
				<ColumnName column={column} />
			</HealthLabel>
		);
	} else if (column === CK.PagesCommonColumn.LighthouseLargestContentfulPaint) {
		return (
			<WebVitalName
				showCoreWebVitalFlag={true}
				type={WebVital.LCP}
			/>
		);
	} else if (column === CK.PagesCommonColumn.LighthouseCumulativeLayoutShift) {
		return (
			<WebVitalName
				showCoreWebVitalFlag={true}
				type={WebVital.CLS}
			/>
		);
	}

	return (
		<ColumnName column={column} />
	);
}



type PageFieldStatisticsChartContentProps = {
	column: CK.PagesColumn,
	data: Array<{
		count: number;
		percentage: number;
		value: any;
	}>,
}

const PageFieldStatisticsChartContent: React.FC<PageFieldStatisticsChartContentProps> = (props) => {
	const {
		column,
		data,
	} = props;

	if (column === CK.PagesCommonColumn.Domain) {
		return (
			<DomainChart
				data={data}
				key={column}
			/>
		);
	}

	if (column === CK.PagesCommonColumn.CanonicalType) {
		return (
			<CanonicalTypeChart
				data={data}
				key={column}
			/>
		);
	}

	if (column === CK.PagesCommonColumn.Health) {
		return (
			<HealthChart
				data={data}
				key={column}
			/>
		);
	}

	if (column === CK.PagesCommonColumn.HreflangLanguage) {
		return (
			<HreflangLanguageChart
				data={data}
				key={column}
			/>
		);
	}

	if (column === CK.PagesCommonColumn.IsDisallowedInRobotsTxt) {
		return (
			<RobotsTxtChart
				data={data}
				key={column}
			/>
		);
	}

	if (
		column === CK.PagesCommonColumn.IsInSitemap
		|| column === CK.PagesCommonColumn.IsIndexable
	) {
		return (
			<BooleanChart
				data={data}
				falseColor="#ff743b"
				key={column}
				name={column}
			/>
		);
	}

	if (
		column === CK.PagesCommonColumn.IsSecured
		|| column === CK.PagesCommonColumn.IsLinked
	) {
		return (
			<BooleanChart
				data={data}
				key={column}
				name={column}
			/>
		);
	}

	if (
		column === CK.PagesCommonColumn.IsIndexableDueToMetaRobots
		|| column === CK.PagesCommonColumn.IsIndexableDueToXRobotsTag
	) {
		return (
			<IndexableChart
				data={data}
				field={column}
				key={column}
			/>
		);
	}

	if (
		column === CK.PagesCommonColumn.LighthouseCumulativeLayoutShift
		|| column === CK.PagesCommonColumn.LighthouseFirstContentfulPaint
		|| column === CK.PagesCommonColumn.LighthouseLargestContentfulPaint
		|| column === CK.PagesCommonColumn.LighthousePerformance
		|| column === CK.PagesCommonColumn.LighthouseSpeedIndex
		|| column === CK.PagesCommonColumn.LighthouseTimeToInteractive
		|| column === CK.PagesCommonColumn.LighthouseTotalBlockingTime
	) {
		return (
			<WebVitalsChart
				data={data}
				field={column}
				key={column}
			/>
		);
	}

	if (column === CK.PagesCommonColumn.NumberOfHreflangs) {
		return (
			<NumberOfHreflangsChart
				data={data}
				key={column}
			/>
		);
	}

	if (
		column === CK.PagesCommonColumn.H1
		|| column === CK.PagesCommonColumn.MetaDescription
		|| column === CK.PagesCommonColumn.OpenGraphDescription
		|| column === CK.PagesCommonColumn.OpenGraphImage
		|| column === CK.PagesCommonColumn.OpenGraphTitle
		|| column === CK.PagesCommonColumn.OpenGraphUrl
		|| column === CK.PagesCommonColumn.Title
		|| column === CK.PagesCommonColumn.TwitterDescription
		|| column === CK.PagesCommonColumn.TwitterImage
		|| column === CK.PagesCommonColumn.TwitterTitle
	) {
		return (
			<ContentPresenceChart
				data={data}
				field={column}
				key={column}
			/>
		);
	}

	if (column === CK.PagesCommonColumn.Relevance) {
		return (
			<RelevanceChart
				data={data}
				key={column}
			/>
		);
	}

	if (column === CK.PagesCommonColumn.StatusCode) {
		return (
			<StatusCodeChart
				data={data}
				key={column}
			/>
		);
	}

	if (column === CK.PagesCommonColumn.Type) {
		return (
			<PageTypeChart
				data={data}
				key={column}
				percentageFactor={100}
			/>
		);
	}

	if (column === CK.PagesCommonColumn.UrlDepth) {
		return (
			<UrlDepthChart
				data={data}
				key={column}
			/>
		);
	}
}
